import clsx from "clsx";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

import { getNavItemStateClasses } from "./navItemStateClasses";

type NavItemLinkProps = {
  link: string;
  label: string;
  /** Use the inverted (light-on-dark) variant. Default: false. */
  inverted?: boolean;
  /** Disables the item. Default: false. */
  disabled?: boolean;
};

/**
 * Link nav item, e.g. a legal link in the footer's Collapsible.
 * Implements all states (default, hovered, pressed, focused, disabled)
 * and both variants (default, inverted).
 *
 * @example
 * ```jsx
 * <NavItemLink link="https://example.com" label="Example" inverted />
 * ```
 *
 * @param link - The URL opened when the link is clicked.
 * @param label - Unique label text displayed in the menu.
 * @param inverted - Use the inverted (light-on-dark) variant. Default: false.
 * @param disabled - Disables the item. Default: false.
 */
export const NavItemLink = ({
  label,
  link,
  inverted = false,
  disabled = false,
}: NavItemLinkProps) => {
  return (
    <a
      data-testid={label}
      className={clsx(
        "group relative block w-fit rounded-md p-2 text-body-base focus:outline-none",
        getNavItemStateClasses({ inverted, disabled }),
        disabled && "pointer-events-none",
      )}
      href={disabled ? undefined : link}
      tabIndex={disabled ? -1 : KEYBOARD_TAB_INDEX}
      target="_blank"
      rel="noreferrer"
    >
      {label}
      <FocusRing inverted={inverted} />
    </a>
  );
};
